import {
  getSearchInvoicesVadIvend,
  getSearchInvoicesVadIvendBranches,
} from 'Api/services/busServices'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useDebounce } from 'use-debounce'

const { createContext, useContext, useState } = require('react')

export const SearchDocumentsVadIvendContext = createContext()

const INITIAL_STATE_FILTERS = {
  integrationDocumentId: null,
  customerName: null,
  documentNumber: null,
  purchaseOrderId: null,
  branchOffices: [],
  companyId: null,
  totalAmount: null,
  salespersonName: null,
  initDate: null,
  endDate: null,
  pageNumber: 1,
  pageSize: 10,
  documentType: null,
  cedula: null,
}

export function SearchDocumentsVadIvendProvider({ children, documentType }) {
  const companyId = useSelector(state => state.company.CompanyId)
  const [dataDocuments, setDataDocuments] = useState([])
  const [dataWareHouse, setDataWareHouse] = useState([])
  const [loadingData, setLoadingData] = useState(false)
  const [errorFormat, setErrorFormat] = useState(false)
  const [isMoreData, setIsMoreData] = useState(true)
  const [filters, setFilters] = useState({
    ...INITIAL_STATE_FILTERS,
    companyId,
    documentType,
  })

  const { pageNumber, ...restFilters } = filters
  const [debouncedFilters] = useDebounce(restFilters, 200)

  const firstLoadRef = useRef(true)

  const updateFilter = useCallback((key, value, type) => {
    if (key === 'companyId') return

    setFilters(prev => {
      const newValue = {
        ...prev,
        [key]: value,
        pageNumber:
          (key !== 'pageNumber' && type === 'filter') || key === 'pageSize'
            ? 1
            : value,
        integrationDocumentId:
          documentType === 'CreditNote'
            ? key === 'integrationDocumentId'
              ? value
              : prev.integrationDocumentId
            : prev.documentNumber,
        documentNumber:
          documentType === 'CreditNote'
            ? key === 'documentNumber'
              ? value
              : prev.documentNumber
            : key === 'documentNumber'
            ? value
            : prev.documentNumber,
      }

      if (JSON.stringify(newValue) === JSON.stringify(prev)) {
        return prev
      }

      return newValue
    })
  }, [])

  async function getDataDocuments(props) {
    if (documentType === 'Invoice') {
      return await getSearchInvoicesVadIvend({ ...props, documentType })
    } else if (documentType === 'CreditNote') {
      return await getSearchInvoicesVadIvend({ ...props, documentType })
    }
  }

  const applyFilters = useCallback(
    async filtersProps => {
      setLoadingData(true)
      try {
        const data = await getDataDocuments(filtersProps)
        setDataDocuments(data?.Data)
      } catch (error) {
        console.error('Error on applyFilters:', error)
      } finally {
        setLoadingData(false)
      }
    },
    [documentType]
  )

  const clearFilters = useCallback(async () => {
    setLoadingData(true)
    try {
      const filterInitialWithBranch = {
        ...INITIAL_STATE_FILTERS,
        companyId,
      }
      setFilters(filterInitialWithBranch)
      const data = await getDataDocuments(filterInitialWithBranch)
      setDataDocuments(data?.Data)
    } catch (error) {
      console.error('Error on clearFilters:', error)
    } finally {
      setLoadingData(false)
    }
  }, [companyId, documentType])

  async function fetchData() {
    setLoadingData(true)
    try {
      const data = await getDataDocuments(filters)
      if (data?.Data?.length > 0) {
        setDataDocuments(data.Data)
        setIsMoreData(true)
      } else {
        if (filters.pageNumber > 1) {
          setIsMoreData(false)
        } else {
          setDataDocuments([])
        }
      }
    } catch (error) {
      console.error('Error on fetchData:', error)
      setDataDocuments([])
    } finally {
      setLoadingData(false)
    }
  }

  function extractValidWarehouses(warehouseInfo) {
    return (warehouseInfo || []).map(branch => ({
      BranchOfficeName: branch.BranchOfficeName,
      BranchOfficeId: branch.BranchOfficeId,
      checked: false,
    }))
  }

  async function fetchWarehouseData() {
    try {
      const warehouseInfo = await getSearchInvoicesVadIvendBranches({
        branchID: companyId,
      })
      const warehouses = extractValidWarehouses(warehouseInfo.Data)
      setDataWareHouse(warehouses)
    } catch (error) {
      console.error('Error on getWarehouseData:', error)
    }
  }

  useEffect(() => {
    setFilters(prev => ({
      ...INITIAL_STATE_FILTERS,
      companyId,
    }))
    setLoadingData(true)
    setDataDocuments([])
    fetchWarehouseData()
  }, [companyId])

  useEffect(() => {
    if (firstLoadRef.current) {
      firstLoadRef.current = false
      return
    }

    if (!errorFormat) fetchData()
  }, [JSON.stringify(debouncedFilters), documentType])

  const value = useMemo(
    () => ({
      updateFilter,
      applyFilters,
      clearFilters,
      setDataDocuments,
      setLoadingData,
      getDataDocuments,
      setFilters,
      setIsMoreData,
      fetchData,
      setDataWareHouse,
      filters,
      isMoreData,
      loadingData,
      dataDocuments,
      dataWareHouse,
      documentType,
      loadingScope: 'table',
      setErrorFormat,
      errorFormat,
    }),
    [
      filters,
      applyFilters,
      clearFilters,
      dataDocuments,
      loadingData,
      dataWareHouse,
      documentType,
      errorFormat,
    ]
  )

  return (
    <SearchDocumentsVadIvendContext.Provider value={value}>
      {children}
    </SearchDocumentsVadIvendContext.Provider>
  )
}

export function useSearchDocumentsVadIvendContext() {
  const context = useContext(SearchDocumentsVadIvendContext)
  if (context === undefined) {
    throw new Error(
      'useSearchDocumentsVadIvendContext must be used within a SearchDocumentsVadIvendProvider'
    )
  }
  return context
}
